import React, { Suspense, useEffect } from 'react';
import FinancialAdviserApp from './components/FinancialAdviserApp';
import AppLoadingScreen from './components/AppLoadingScreen';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import { lazyPage } from './utils/lazyPage';
import './index.css';

// Public pages (no sign-in) load only when visited.
const DocuSignConsentPage = lazyPage(() => import('./components/DocuSignConsentPage'));
const DocuSignReturnPage = lazyPage(() => import('./components/DocuSignReturnPage'));
const LandingPage = lazyPage(() => import('./components/landing/LandingPage'));
const ContactPage = lazyPage(() => import('./components/landing/ContactPage'));
const ClientIdUploadPage = lazyPage(() => import('./components/idUpload/ClientIdUploadPage'));

function App() {
  // Suppress generic "Script error" messages that can occur from external scripts
  useEffect(() => {
    const handleError = (event) => {
      if (event.message === 'Script error.' || event.message === 'Script error') {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      const reason = event.reason;
      if (reason && typeof reason === 'string' && reason.includes('Script error')) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Public marketing landing — no auth
  if (typeof window !== 'undefined' && window.location.pathname === '/welcome') {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Suspense fallback={<AppLoadingScreen />}>
            <LandingPage />
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Public contact / book-a-demo form — no auth
  if (typeof window !== 'undefined' && window.location.pathname === '/contact') {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Suspense fallback={<AppLoadingScreen />}>
            <ContactPage />
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // Public secure ID/TFN upload page — the client has no account, so no auth.
  // Authorisation is the ?t= link token plus the date-of-birth gate.
  if (typeof window !== 'undefined' && window.location.pathname === '/id-upload') {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Suspense fallback={<AppLoadingScreen />}>
            <ClientIdUploadPage />
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // DocuSign redirects here after user grants JWT consent; show success and link back to app
  if (typeof window !== 'undefined' && window.location.pathname === '/docusign-consent') {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Suspense fallback={<AppLoadingScreen />}>
            <DocuSignConsentPage />
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  // DocuSign Sender View redirects here after tab placement; show confirmation page
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('docusign_return') === 'true') {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <Suspense fallback={<AppLoadingScreen />}>
            <DocuSignReturnPage />
          </Suspense>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
    <ThemeProvider>
      <div className="App">
        <FinancialAdviserApp />
      </div>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
